// Data sources — frontend wrappers for the data_sources Rust commands.
//
// Mirrors lib/tauri.ts: dynamic import of @tauri-apps/api so plain Vite dev
// still serves a usable UI. Browser-dev path returns empty lists / a soft
// error from test/save so the Settings page renders something to look at.

import type {
  DataSourceSafe,
  DataSourceSpec,
  FunnelSnapshotWire,
  TestConnectionInfo,
} from "./types";

const hasTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const mod = await import("@tauri-apps/api/core");
  return mod.invoke<T>(cmd, args);
}

export async function listDataSources(): Promise<DataSourceSafe[]> {
  if (!hasTauri) {
    const raw = localStorage.getItem("bvndle-cc:data-sources");
    return raw ? JSON.parse(raw) : [];
  }
  return invoke<DataSourceSafe[]>("list_data_sources");
}

export async function saveDataSource(spec: DataSourceSpec): Promise<DataSourceSafe> {
  if (!hasTauri) {
    const list = (await listDataSources()) as DataSourceSafe[];
    const safe: DataSourceSafe = {
      id: spec.id || `ds-${Date.now().toString(16)}`,
      label: spec.label,
      kind: spec.kind,
      auth_kind: spec.auth.kind,
      created_at: new Date().toISOString(),
    };
    const next = list.filter((s) => s.id !== safe.id).concat(safe);
    localStorage.setItem("bvndle-cc:data-sources", JSON.stringify(next));
    return safe;
  }
  return invoke<DataSourceSafe>("save_data_source", { spec });
}

export async function deleteDataSource(id: string): Promise<void> {
  if (!hasTauri) {
    const list = await listDataSources();
    localStorage.setItem(
      "bvndle-cc:data-sources",
      JSON.stringify(list.filter((s) => s.id !== id)),
    );
    return;
  }
  await invoke<void>("delete_data_source", { id });
}

export async function testDataSource(spec: DataSourceSpec): Promise<TestConnectionInfo> {
  if (!hasTauri) {
    throw new Error("Test connection is only available inside the Tauri app");
  }
  return invoke<TestConnectionInfo>("test_data_source", { spec });
}

export async function testSavedDataSource(id: string): Promise<TestConnectionInfo> {
  if (!hasTauri) {
    throw new Error("Test connection is only available inside the Tauri app");
  }
  return invoke<TestConnectionInfo>("test_saved_data_source", { id });
}

export async function fetchFunnelSnapshot(forceRefresh = false): Promise<FunnelSnapshotWire> {
  if (!hasTauri) {
    // Browser-dev: synthetic snapshot so the page renders something.
    return {
      pulled_at: new Date().toISOString(),
      cache_status: "fresh",
      stages: [
        { id: "vfd_reach", label: "VFD reach", description: "Synthetic browser-dev value.", is_cliff: false, status: "ok", count: 5_400_000, denominator_id: null, error: null, elapsed_ms: 12 },
        { id: "bvndle_signup", label: "Bvndle signup", description: "Synthetic browser-dev value.", is_cliff: true, status: "ok", count: 192_093, denominator_id: "vfd_reach", error: null, elapsed_ms: 14 },
        { id: "first_earn", label: "First earn", description: "Synthetic browser-dev value.", is_cliff: false, status: "ok", count: 124_744, denominator_id: "bvndle_signup", error: null, elapsed_ms: 11 },
        { id: "first_redeem", label: "First redeem", description: "Synthetic browser-dev value.", is_cliff: true, status: "ok", count: 29_166, denominator_id: "first_earn", error: null, elapsed_ms: 13 },
        { id: "repeat_redeem", label: "Repeat redeem", description: "Synthetic browser-dev value.", is_cliff: false, status: "ok", count: 19_850, denominator_id: "first_redeem", error: null, elapsed_ms: 9 },
        { id: "habit", label: "Habit · 3+ in 90d", description: "Synthetic browser-dev value.", is_cliff: false, status: "ok", count: 14_873, denominator_id: "first_redeem", error: null, elapsed_ms: 10 },
      ],
    };
  }
  return invoke<FunnelSnapshotWire>("funnel_snapshot", { forceRefresh });
}
